import {Trans} from '@lingui/macro';
import _, {chunk, Dictionary, findLast, groupBy, isUndefined} from 'lodash';
import memoize from 'lru-memoize';
import React, {createRef, PureComponent, RefObject} from 'react';
import {ListOnScrollProps, VariableSizeList} from 'react-window';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import emptyEmoji from '../../../../assets/images/emptyEmoji70.svg';
import {SkinTonesEnum} from '../../../../models/api/preferencesModel';
import {EmojiCategoriesEnum} from '../../../../services/emojis/emojiList';
import {EmojiDefinition, isEmojiCategoriesEnum} from '../../../../services/emojis/emojis';
import {EmojiSearcher} from '../../../../store/stateless/emojiSelectors';
import {DropdownEmpty} from '../dropdownEmpty';
import {EmojiDropdownListData, EmojiDropdownListRow, emojiPerRow} from './emojiDropdownListRow';

/*
 * Props.
 */

interface EmojiDropdownListProps {
  skinTone: SkinTonesEnum;
  frequentlyUsedEmoji: ReadonlyArray<EmojiDefinition>;
  emojiList: ReadonlyArray<EmojiDefinition>;
  emojiSearcher: EmojiSearcher;
  searchQuery: string;
  onEmojiMouseEnter?: HandlerOf<EmojiDefinition>;
  onEmojiMouseLeave?: Handler;
  onEmojiClick?: HandlerOf<EmojiDefinition>;
  onCategoryChange?: HandlerOf<EmojiCategoriesEnum>;
}

/*
 * Style.
 */

const emojiRowHeight = 34;
const categoryRowHeight = 30;
const innerWidth = 345;
const innerHeight = 250;

const StyledVariableSizeList = styled(VariableSizeList)`
  padding: 4px 12px 4px 12px;
  overflow-y: scroll !important;

  /* Sizer element. */
  &::before {
    content: '';
    display: block;
    width: ${innerWidth}px;
  }

  /* Internal element. */
  & > div {
    position: relative;
  }
`;

const StyledEmptyDiv = styled.div`
  overflow-y: scroll;
  padding: 0 12px;
  height: ${innerHeight}px;

  /* Sizer element. */
  &::before {
    content: '';
    display: block;
    width: ${innerWidth}px;
  }

  ${center()};
`;

/*
 * Component.
 */

export class EmojiDropdownList extends PureComponent<EmojiDropdownListProps> {
  constructor(props: EmojiDropdownListProps) {
    super(props);
    this.listRef = createRef<VariableSizeList>();
  }

  private readonly listRef: RefObject<VariableSizeList>;

  /*
   * Ref API.
   */

  scrollTo(category: EmojiCategoriesEnum) {
    // We need a ref to the list to do anything.
    const currentList = this.listRef.current;
    if (!currentList) {
      return;
    }

    // Find the index of the row to scroll to.
    const {frequentlyUsedEmoji, emojiList} = this.props;
    const categoryPosition = getCategoryPositions(emojiList, frequentlyUsedEmoji).find(
      (c) => c.category === category,
    );
    const categoryIndex = categoryPosition && categoryPosition.index;
    if (isUndefined(categoryIndex)) {
      return;
    }

    // Scroll to it.
    currentList.scrollToItem(categoryIndex, 'start');
  }

  /*
   * Event handlers.
   */

  private readonly onListScroll = (listData: EmojiDropdownListData, scrollProps: ListOnScrollProps) => {
    // No need to do anything if we're in search mode.
    const {searchQuery, onCategoryChange, emojiList, frequentlyUsedEmoji} = this.props;
    if (searchQuery || !onCategoryChange) {
      return;
    }

    // We need a ref to the list to do anything.
    const currentList = this.listRef.current;
    if (!currentList) {
      return;
    }

    // Find the currently active category.
    const position = findLast(getCategoryPositions(emojiList, frequentlyUsedEmoji), ({index}) => {
      const offset = this.findRowOffset(listData, index);
      return scrollProps.scrollOffset >= offset;
    });

    // Notify about the current active category.
    const activeCategory = position ? position.category : EmojiCategoriesEnum.FLAGS;
    onCategoryChange(activeCategory);
  };

  private readonly findRowOffset = memoize(20)((listData: EmojiDropdownListData, index: number) => {
    let offset = 0;
    let currentIndex = 0;

    while (currentIndex < index) {
      offset += findRowHeight(listData, currentIndex);
      currentIndex++;
    }

    return offset;
  });

  private readonly onEmojiMouseEnter: HandlerOf<EmojiDefinition> = (emoji) => {
    const {onEmojiMouseEnter} = this.props;
    return onEmojiMouseEnter && onEmojiMouseEnter(emoji);
  };

  private readonly onEmojiMouseLeave: Handler = () => {
    const {onEmojiMouseLeave} = this.props;
    return onEmojiMouseLeave && onEmojiMouseLeave();
  };

  private readonly onEmojiClick: HandlerOf<EmojiDefinition> = (emoji) => {
    const {onEmojiClick} = this.props;
    return onEmojiClick && onEmojiClick(emoji);
  };

  /*
   * Render.
   */

  render() {
    // Find the list of items to render.
    const {skinTone, emojiSearcher, frequentlyUsedEmoji, searchQuery, emojiList} = this.props;
    const listData = searchQuery
      ? getSearchListData(emojiSearcher, searchQuery)
      : getFullListData(emojiList, frequentlyUsedEmoji);

    // Empty search state.
    if (listData.length === 0) {
      return (
        <StyledEmptyDiv>
          <DropdownEmpty imageSource={emptyEmoji}>
            <Trans id="emoji-dropdown-noresults">No emoji found</Trans>
          </DropdownEmpty>
        </StyledEmptyDiv>
      );
    }

    return (
      <StyledVariableSizeList
        key={searchQuery ? 'search' : 'list'}
        ref={this.listRef}
        width="auto"
        height={innerHeight}
        estimatedItemSize={emojiRowHeight}
        itemSize={(i) => findRowHeight(listData, i)}
        itemCount={listData.length}
        onScroll={(scrollProps) => this.onListScroll(listData, scrollProps)}
      >
        {(rowProps) => (
          <EmojiDropdownListRow
            {...rowProps}
            listData={listData}
            skinTone={skinTone}
            onEmojiMouseEnter={this.onEmojiMouseEnter}
            onEmojiMouseLeave={this.onEmojiMouseLeave}
            onEmojiClick={this.onEmojiClick}
          />
        )}
      </StyledVariableSizeList>
    );
  }
}

/*
 * Helpers.
 */

function findRowHeight(listData: EmojiDropdownListData, index: number) {
  const row = listData[index];
  return isEmojiCategoriesEnum(row) ? categoryRowHeight : emojiRowHeight;
}

/*
 * Emoji data preparation.
 */

/** Build the full list of rows with emoji and categories. */
const getFullListData = memoize()(
  (emojiList: readonly EmojiDefinition[], frequentlyUsedEmoji: ReadonlyArray<EmojiDefinition>) => {
    // Group the emoji by category.
    const emojiByCategory: Dictionary<ReadonlyArray<EmojiDefinition>> = groupBy(emojiList, (e) => e.category);

    // If some were provided, add the frequently used emoji.
    if (frequentlyUsedEmoji.length > 0) {
      emojiByCategory[EmojiCategoriesEnum.FREQUENTLY_USED] = frequentlyUsedEmoji;
    }

    // Return the categories in order, with the emoji lines in the middle.
    return _(EmojiCategoriesEnum)
      .filter((c) => Boolean(emojiByCategory[c] && emojiByCategory[c].length))
      .map((category) => [category, ...chunk(emojiByCategory[category], emojiPerRow)])
      .flatten()
      .value();
  },
);

/** Build the list of rows for search results. */
const getSearchListData = memoize(20)((emojiSearcher: EmojiSearcher, searchQuery: string) => {
  const normalizedSearchQuery = searchQuery.toLowerCase().replace(/\W/g, '');
  return chunk(
    emojiSearcher(normalizedSearchQuery, 50).map((e) => e.emoji),
    emojiPerRow,
  );
});

/** Build an array of categories and their row index. */
const getCategoryPositions = memoize()(
  (emojiList: readonly EmojiDefinition[], frequentlyUsedEmoji: ReadonlyArray<EmojiDefinition>) => {
    const listData = getFullListData(emojiList, frequentlyUsedEmoji);
    return _(EmojiCategoriesEnum)
      .filter((c) => Boolean(listData.includes(c)))
      .map((c) => ({
        category: c,
        index: listData.indexOf(c),
      }))
      .value();
  },
);
